import React, { useEffect, useState } from 'react';
import API from '../utils/api';
import { toast } from 'react-toastify';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

const EMPTY_FORM = { amount: '', amountLabel: '', title: '', description: '', icon: '', accentColor: '#FF9933', order: 0 };

export default function ManageDonationWays() {
  const [ways, setWays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [iconFile, setIconFile] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchWays = () => {
    setLoading(true);
    API.get('/donation-ways/admin').then(r => { setWays(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchWays(); }, []);

  const resetForm = () => { setForm(EMPTY_FORM); setIconFile(null); setEditingId(null); };
  const openAddForm = () => { resetForm(); setShowForm(true); };
  const openEditForm = (w) => {
    setEditingId(w._id);
    setForm({
      amount: w.amount ?? '', amountLabel: w.amountLabel || '', title: w.title, description: w.description,
      icon: w.icon || '', accentColor: w.accentColor || '#FF9933', order: w.order || 0,
    });
    setIconFile(null);
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); resetForm(); };

  const iconIsImage = icon => !!icon && (icon.startsWith('/uploads/') || icon.startsWith('http'));
  const iconUrl = icon => iconIsImage(icon) ? (icon.startsWith('http') ? icon : `${API_BASE}${icon}`) : null;

  const handleSave = async () => {
    if (!form.title.trim()) return toast.error('Title is required');
    if (!form.description.trim()) return toast.error('Description is required');
    if (!form.amount && !form.amountLabel.trim()) return toast.error('Set either an amount or an amount label (e.g. "Any")');
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === 'icon') return; // icon text value only sent when there's no new file — handled below
        fd.append(k, v);
      });
      if (iconFile) fd.append('iconImage', iconFile);
      else fd.append('icon', form.icon); // keep/replace the plain emoji/text icon
      if (editingId) {
        await API.put(`/donation-ways/${editingId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Donation way updated');
      } else {
        await API.post('/donation-ways', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Donation way added');
      }
      closeForm();
      fetchWays();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error saving');
    }
    setSaving(false);
  };

  const handleTogglePublish = async (w) => {
    try {
      await API.put(`/donation-ways/${w._id}`, { isActive: !w.isActive });
      toast.success(w.isActive ? 'Moved to draft' : 'Published');
      fetchWays();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error updating status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Permanently delete this donation option? This cannot be undone.')) return;
    try {
      await API.delete(`/donation-ways/${id}`);
      toast.success('Deleted');
      fetchWays();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error deleting');
    }
  };

  const displayAmount = w => w.amountLabel || (w.amount ? `₹${Number(w.amount).toLocaleString('en-IN')}` : '—');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 className="admin-page-title" style={{ margin: 0 }}>🎁 Manage Donation Ways</h2>
        <button className="btn btn-primary" onClick={() => (showForm ? closeForm() : openAddForm())}>
          {showForm ? 'Cancel' : '+ Add Donation Way'}
        </button>
      </div>
      <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: 24 }}>
        These are the "Ways to Donate" cards on the homepage (1 Month Fodder, Adopt a Cow, etc.) — icon, amount, and text are all editable here.
      </p>

      {showForm && (
        <div className="admin-form-card" style={{ marginBottom: 24 }}>
          <h3 style={{ fontWeight: 700, marginBottom: 16 }}>{editingId ? 'Edit Donation Way' : 'Add Donation Way'}</h3>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Amount (₹)</label>
              <input className="form-input" type="number" placeholder="e.g. 1100" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Amount Label (overrides amount)</label>
              <input className="form-input" placeholder='e.g. "Any"' value={form.amountLabel} onChange={e => setForm(p => ({ ...p, amountLabel: e.target.value }))} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Title *</label>
            <input className="form-input" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Description *</label>
            <textarea className="form-input" rows={2} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Icon (emoji or symbol)</label>
              <input className="form-input" placeholder="🐂" value={form.icon} onChange={e => setForm(p => ({ ...p, icon: e.target.value }))} disabled={!!iconFile} />
              <div style={{ fontSize: '0.78rem', color: '#888', marginTop: 4 }}>Used only if no custom image is uploaded below.</div>
            </div>
            <div className="form-group">
              <label className="form-label">Custom Icon Image (optional)</label>
              {iconIsImage(form.icon) && !iconFile && (
                <div style={{ marginBottom: 8 }}>
                  <img src={iconUrl(form.icon)} alt="Current icon" style={{ width: 40, height: 40, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 6 }} />
                </div>
              )}
              <input type="file" accept="image/*" onChange={e => setIconFile(e.target.files[0])} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Accent Color</label>
              <input type="color" value={form.accentColor} onChange={e => setForm(p => ({ ...p, accentColor: e.target.value }))} style={{ width: 60, height: 40, padding: 2, cursor: 'pointer' }} />
            </div>
            <div className="form-group">
              <label className="form-label">Display Order</label>
              <input className="form-input" type="number" value={form.order} onChange={e => setForm(p => ({ ...p, order: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editingId ? 'Update' : 'Save'}
            </button>
            <button className="btn btn-outline" onClick={closeForm}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="loader"><div className="spinner" /></div> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
          {ways.map(w => (
            <div key={w._id} style={{ background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', position: 'relative', textAlign: 'center', padding: '20px 14px 14px', borderTop: `4px solid ${w.accentColor || '#FF9933'}`, opacity: w.isActive ? 1 : 0.6 }}>
              <span className={`badge ${w.isActive ? 'badge-green' : 'badge-orange'}`} style={{ position: 'absolute', top: 10, left: 10 }}>
                {w.isActive ? 'Published' : 'Draft'}
              </span>
              <div style={{ marginTop: 20, fontSize: '2rem', marginBottom: 8 }}>
                {iconUrl(w.icon) ? <img src={iconUrl(w.icon)} alt={w.title} style={{ width: 40, height: 40, objectFit: 'contain', margin: '0 auto' }} /> : (w.icon || '🎁')}
              </div>
              <div style={{ fontWeight: 700, fontSize: '1.05rem', color: w.accentColor || '#FF9933' }}>{displayAmount(w)}</div>
              <div style={{ fontWeight: 600, fontSize: '0.9rem', marginTop: 4 }}>{w.title}</div>
              <div style={{ fontSize: '0.78rem', color: '#888', marginTop: 4, marginBottom: 14 }}>{w.description}</div>

              <div style={{ display: 'flex', justifyContent: 'center', gap: 6, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
                <button title="View" onClick={() => setViewing(w)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, width: 30, height: 30, cursor: 'pointer' }}>👁</button>
                <button title="Edit" onClick={() => openEditForm(w)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, width: 30, height: 30, cursor: 'pointer' }}>✏️</button>
                <button title={w.isActive ? 'Move to Draft' : 'Publish'} onClick={() => handleTogglePublish(w)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, width: 30, height: 30, cursor: 'pointer' }}>
                  {w.isActive ? '📦' : '📢'}
                </button>
                <button title="Delete" onClick={() => handleDelete(w._id)} style={{ background: 'none', border: '1px solid #f5c6c6', color: '#c62828', borderRadius: 6, width: 30, height: 30, cursor: 'pointer' }}>🗑</button>
              </div>
            </div>
          ))}
          {ways.length === 0 && (
            <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 40, color: '#888' }}>No donation ways added yet.</div>
          )}
        </div>
      )}

      {viewing && (
        <div className="modal-overlay" onClick={() => setViewing(null)}>
          <div className="modal-card auth-card" style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
            <button type="button" className="modal-close" onClick={() => setViewing(null)} aria-label="Close">✕</button>
            <div style={{ fontSize: '2.5rem', marginBottom: 10 }}>
              {iconUrl(viewing.icon) ? <img src={iconUrl(viewing.icon)} alt={viewing.title} style={{ width: 56, height: 56, objectFit: 'contain', margin: '0 auto' }} /> : (viewing.icon || '🎁')}
            </div>
            <div style={{ fontWeight: 700, fontSize: '1.3rem', color: viewing.accentColor || '#FF9933' }}>{displayAmount(viewing)}</div>
            <h3 style={{ fontWeight: 700, marginTop: 6 }}>{viewing.title}</h3>
            <p style={{ marginTop: 10, color: 'var(--text-mid)', fontSize: '0.9rem', lineHeight: 1.6, textAlign: 'left' }}>{viewing.description}</p>
            <span className={`badge ${viewing.isActive ? 'badge-green' : 'badge-orange'}`} style={{ display: 'inline-block', marginTop: 10 }}>
              {viewing.isActive ? 'Published' : 'Draft'}
            </span>
            <div style={{ marginTop: 16, fontSize: '0.8rem', color: '#999' }}>Display order: {viewing.order ?? 0}</div>
          </div>
        </div>
      )}
    </div>
  );
}
